import React, { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import Navigation from '../components/Navigation'
import MaterialMenu from '../components/MaterialMenu'
import { getAllCourses, subscribeToCourseSections, deleteSection, getCourseMaterials, getCourseNotes } from '../services/firestore'
import { getTutorProfile } from '../services/firestore'
import { Search, BookOpen, Users, Clock, MapPin, Loader, Filter, X, Calendar, TrendingUp, Trash2, ExternalLink, Video, FileText, Book, Microscope, StickyNote as StickyNoteIcon } from 'lucide-react'
import MaterialCard from '../components/MaterialCard'
import StickyNote from '../components/StickyNote'
import { capitalizeName } from '../utils/formatters'
import './Sections.css'

// Cache para guardar perfiles de tutores y no pedirlos múltiples veces entre navegaciones
const globalTutorProfilesCache = {}

const Sections = () => {
  const { user } = useAuth()
  const [courses, setCourses] = useState({})
  const [coursesList, setCoursesList] = useState([])
  const [selectedCourse, setSelectedCourse] = useState(null)
  const [isMaterialMenuOpen, setIsMaterialMenuOpen] = useState(false)
  const [loading, setLoading] = useState(true)
  const [showFilters, setShowFilters] = useState(false)
  const [viewMode, setViewMode] = useState('list') // 'list' o 'calendar'
  const [filters, setFilters] = useState({
    tutor: '',
    day: '',
    course: '',
    search: ''
  })

  const formatDate = (dateStr) => {
    if (!dateStr) return ''
    try {
      const parts = dateStr.split('-')
      if (parts.length !== 3) return dateStr
      const [, month, day] = parts
      const dayInt = parseInt(day)
      const monthInt = parseInt(month)
      if (isNaN(dayInt) || isNaN(monthInt) || monthInt < 1 || monthInt > 12) {
        return dateStr
      }
      const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']
      return `${dayInt} ${months[monthInt - 1]}`
    } catch {
      return dateStr
    }
  }

  // Materials & Notes
  const [showMaterialsModal, setShowMaterialsModal] = useState(false)
  const [currentCourseMaterials, setCurrentCourseMaterials] = useState([])
  const [currentCourseNotes, setCurrentCourseNotes] = useState([])

  useEffect(() => {
    loadCourses()
  }, [])

  // Suscribirse en tiempo real a las secciones de la clase seleccionada
  useEffect(() => {
    if (!selectedCourse || !courses[selectedCourse]) return

    const courseId = selectedCourse
    
    const unsubscribe = subscribeToCourseSections(courseId, (result) => {
      if (result.success) {
        setCourses(prev => {
          // Solo actualizar el estado si los datos realmente cambiaron
          const prevSections = prev[selectedCourse]?.sections || []
          if (JSON.stringify(prevSections) === JSON.stringify(result.data)) {
            return prev
          }
          return {
            ...prev,
            [selectedCourse]: {
              ...prev[selectedCourse],
              sections: result.data,
              sectionsLoaded: true
            }
          }
        })
      }
    })

    return () => unsubscribe()
  }, [selectedCourse, courses[selectedCourse]])


  const loadCourses = async () => {
    try {
      setLoading(true)
      const result = await getAllCourses()
      if (result.success) {
        await processCourses(result.data)
      }
    } catch (error) {
      console.error('Error loading courses:', error)
    } finally {
      setLoading(false)
    }
  }

  const processCourses = async (coursesData) => {
    const coursesMap = {}
    const coursesIds = []

    // Usar caché para agilizar el procesamiento
    const localTutorCache = globalTutorProfilesCache

    // Procesar todos los cursos en paralelo
    const processedCourses = await Promise.all(coursesData.map(async (course) => {
      let tutorsInfo = []
      if (course.tutors && Array.isArray(course.tutors)) {
        const tutorPromises = course.tutors.map(async (tutorId) => {
          if (localTutorCache[tutorId]) {
            return localTutorCache[tutorId]
          }
          try {
            const tutorResult = await getTutorProfile(tutorId)
            let newTutorInfo = { id: tutorId, name: tutorId }
            if (tutorResult.success && tutorResult.data.fullName) {
              newTutorInfo.name = capitalizeName(tutorResult.data.fullName)
            }
            localTutorCache[tutorId] = newTutorInfo
            return newTutorInfo
          } catch {
            return { id: tutorId, name: tutorId }
          }
        })
        tutorsInfo = await Promise.all(tutorPromises)
      }

      return {
        id: course.id,
        data: {
          id: course.id,
          name: course.name,
          tutors: tutorsInfo,
          description: course.description || '',
          whatsappLink: course.whatsappLink || null,
          category: course.category || 'SIN CATEGORIA',
          sections: courses[course.id]?.sections || [],
          sectionsLoaded: courses[course.id]?.sectionsLoaded || false
        }
      }
    }))

    for (const pc of processedCourses) {
      coursesMap[pc.id] = pc.data
      coursesIds.push(pc.id)
    }

    setCourses(coursesMap)
    setCoursesList(coursesIds)

    // Seleccionar el primer curso si no hay uno seleccionado
    if (!selectedCourse && coursesIds.length > 0) {
      setSelectedCourse(coursesIds[0])
    }
  }



  const handleCourseSelect = (courseId) => {
    setSelectedCourse(courseId)
    setFilters(prev => ({ ...prev, course: courses[courseId]?.name || '' }))
  }

  // Definir currentCourse antes de usarlo en useMemo
  const currentCourse = selectedCourse ? courses[selectedCourse] : null

  // Obtener todos los tutores y días únicos para filtros
  const allTutors = useMemo(() => {
    const tutorsSet = new Set()
    Object.values(courses).forEach(course => {
      course.tutors?.forEach(tutor => tutorsSet.add(tutor.name))
    })
    return Array.from(tutorsSet).sort()
  }, [courses])

  const allDays = useMemo(() => {
    const daysSet = new Set()
    Object.values(courses).forEach(course => {
      course.sections?.forEach(section => {
        if (section.day) daysSet.add(section.day)
      })
    })
    return Array.from(daysSet).sort()
  }, [courses])

  // Filtrar secciones según los filtros
  const filteredSections = useMemo(() => {
    if (!currentCourse) return []

    let sections = currentCourse.sections || []

    // Filtro por tutor
    if (filters.tutor) {
      sections = sections.filter(section => {
        const sectionTutor = currentCourse.tutors?.find(t => t.id === section.createdBy)
        return sectionTutor?.name === filters.tutor
      })
    }

    // Filtro por día
    if (filters.day) {
      sections = sections.filter(section =>
        section.day?.toLowerCase().includes(filters.day.toLowerCase())
      )
    }

    // Filtro por búsqueda
    if (filters.search) {
      const searchLower = filters.search.toLowerCase()
      sections = sections.filter(section =>
        section.day?.toLowerCase().includes(searchLower) ||
        section.time?.toLowerCase().includes(searchLower) ||
        section.location?.toLowerCase().includes(searchLower)
      )
    }

    return sections
  }, [currentCourse, filters])


  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }))
  }

  const clearFilters = () => {
    setFilters({
      tutor: '',
      day: '',
      course: '',
      search: ''
    })
  }

  const handleDeleteSection = async (sectionId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta sección?')) {
      return
    }

    try {
      const result = await deleteSection(sectionId)
      if (!result.success) {
        alert('Error al eliminar la sección: ' + result.error)
      }
      // La sección se eliminará automáticamente gracias a la suscripción en tiempo real
    } catch (err) {
      alert('Error al eliminar la sección')
      console.error(err)
    }
  }

  const handleShowMaterials = async (courseId) => {
    try {
      const materialsResult = await getCourseMaterials(courseId)
      const notesResult = await getCourseNotes(courseId)

      if (materialsResult.success) {
        setCurrentCourseMaterials(materialsResult.data)
      }
      if (notesResult.success) {
        setCurrentCourseNotes(notesResult.data)
      }

      setShowMaterialsModal(true)
    } catch (err) {
      console.error('Error loading materials:', err)
    }
  }

  const materialsByType = {
    video: currentCourseMaterials.filter(m => m.type === 'video'),
    ejercicio: currentCourseMaterials.filter(m => m.type === 'ejercicio'),
    libro: currentCourseMaterials.filter(m => m.type === 'libro'),
    simulador: currentCourseMaterials.filter(m => m.type === 'simulador')
  }

  const hasActiveFilters = filters.tutor || filters.day || filters.search

  if (loading) {
    return (
      <div className="sections-page">
        <div className="loading-container">
          <div className="custom-loader">
            <div className="loader-circle"></div>
          </div>
          <p>Cargando cursos...</p>
        </div>
      </div>
    )
  }

  if (!currentCourse || coursesList.length === 0) {
    return (
      <div className="sections-page">
        <div className="empty-state">
          <BookOpen size={64} />
          <h2>No hay cursos disponibles</h2>
          <p>Los tutores aún no han creado clases</p>
        </div>
      </div>
    )
  }


  return (
    <div className="sections-page">
      <div className="sections-layout">
        <Navigation
          onCourseSelect={handleCourseSelect}
          selectedCourse={selectedCourse}
          coursesList={coursesList}
          coursesData={courses}
        />
        <div className="content-area">
          <div className="content-header">
            <button
              className="materials-button"
              onClick={() => handleShowMaterials(currentCourse.id)}
              title="Ver materiales del curso"
            >
              <BookOpen size={20} />
              Material de {currentCourse?.name || ''}
            </button>
            <div className="header-actions">
              <a
                href={currentCourse.whatsappLink || "https://chat.whatsapp.com/Hxob8TLubdI9NTIAaYxWB7"}
                target="_blank"
                rel="noopener noreferrer"
                className="whatsapp-button"
              >
                <img
                  src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg"
                  alt="WhatsApp"
                  className="whatsapp-icon"
                />
                Únete al grupo de WhatsApp
              </a>
              <div className="view-controls">
                <button
                  className={`view-toggle ${viewMode === 'list' ? 'active' : ''}`}
                  onClick={() => setViewMode('list')}
                  title="Vista de lista"
                >
                  <BookOpen size={18} />
                </button>
                <button
                  className={`view-toggle ${viewMode === 'calendar' ? 'active' : ''}`}
                  onClick={() => setViewMode('calendar')}
                  title="Vista de calendario"
                >
                  <Calendar size={18} />
                </button>
                <button
                  className={`filter-toggle ${showFilters ? 'active' : ''}`}
                  onClick={() => setShowFilters(!showFilters)}
                  title="Filtros"
                >
                  <Filter size={18} />
                  {hasActiveFilters && <span className="filter-badge"></span>}
                </button>
              </div>
            </div>
          </div>

          {/* Panel de Filtros */}
          {showFilters && (
            <div className="filters-panel">
              <div className="filters-header">
                <h3>
                  <Filter size={20} />
                  Filtros de Búsqueda
                </h3>
                {hasActiveFilters && (
                  <button className="clear-filters-btn" onClick={clearFilters}>
                    <X size={16} />
                    Limpiar filtros
                  </button>
                )}
              </div>

              <div className="filters-grid">
                <div className="filter-group">
                  <label>
                    <Search size={16} />
                    Búsqueda general
                  </label>
                  <input
                    type="text"
                    placeholder="Buscar por día, horario, ubicación..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                  />
                </div>

                <div className="filter-group">
                  <label>
                    <Users size={16} />
                    Filtrar por Tutor
                  </label>
                  <select
                    value={filters.tutor}
                    onChange={(e) => handleFilterChange('tutor', e.target.value)}
                  >
                    <option value="">Todos los tutores</option>
                    {allTutors.map(tutor => (
                      <option key={tutor} value={tutor}>{tutor}</option>
                    ))}
                  </select>
                </div>

                <div className="filter-group">
                  <label>
                    <Clock size={16} />
                    Filtrar por Día
                  </label>
                  <select
                    value={filters.day}
                    onChange={(e) => handleFilterChange('day', e.target.value)}
                  >
                    <option value="">Todos los días</option>
                    {allDays.map(day => (
                      <option key={day} value={day}>{day}</option>
                    ))}
                  </select>
                </div>

                <div className="filter-group">
                  <label>
                    <BookOpen size={16} />
                    Filtrar por Clase
                  </label>
                  <select
                    value={filters.course || selectedCourse}
                    onChange={(e) => {
                      if (e.target.value) {
                        setSelectedCourse(e.target.value)
                        handleFilterChange('course', courses[e.target.value]?.name || '')
                      } else {
                        handleFilterChange('course', '')
                      }
                    }}
                  >
                    <option value="">Todas las clases</option>
                    {coursesList.map(courseId => (
                      <option key={courseId} value={courseId}>{courses[courseId]?.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {hasActiveFilters && (
                <div className="active-filters">
                  <span>Filtros activos:</span>
                  {filters.tutor && (
                    <span className="filter-tag">
                      Tutor: {filters.tutor}
                      <button onClick={() => handleFilterChange('tutor', '')}>
                        <X size={12} />
                      </button>
                    </span>
                  )}
                  {filters.day && (
                    <span className="filter-tag">
                      Día: {filters.day}
                      <button onClick={() => handleFilterChange('day', '')}>
                        <X size={12} />
                      </button>
                    </span>
                  )}
                  {filters.search && (
                    <span className="filter-tag">
                      Búsqueda: {filters.search}
                      <button onClick={() => handleFilterChange('search', '')}>
                        <X size={12} />
                      </button>
                    </span>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="course-details">
            <h1 className="course-title">{currentCourse?.name}</h1>

            {currentCourse.description && (
              <div className="course-description">
                <p>{currentCourse.description}</p>
              </div>
            )}

            <div className="course-info">
              <div className="tutors-section">
                <h3>
                  <Users size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                  Tutores
                </h3>
                {currentCourse.tutors && currentCourse.tutors.length > 0 ? (
                  <ul className="tutors-list">
                    {currentCourse.tutors.map((tutor, index) => (
                      <li key={tutor.id || index}>
                        <Link to={`/tutor/${tutor.id}`} className="tutor-link">
                          {tutor.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="no-tutors">Sin tutores asignados</p>
                )}
              </div>
            </div>

            <div className="sections-schedule">
              <div className="schedule-header">
                <h3>
                  <Clock size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                  Horarios de Clase
                  {hasActiveFilters && (
                    <span className="filtered-count">
                      ({filteredSections.length} de {currentCourse.sections?.length || 0})
                    </span>
                  )}
                </h3>
              </div>

              {viewMode === 'calendar' ? (
                <div className="calendar-view">
                  <div className="calendar-grid">
                    {['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'].map(day => {
                      const daySections = filteredSections.filter(s =>
                        s.day?.toLowerCase().includes(day.toLowerCase())
                      )
                      return (
                        <div key={day} className="calendar-day">
                          <div className="calendar-day-header">{day}</div>
                          <div className="calendar-day-sections">
                            {daySections.length > 0 ? (
                              daySections.map((section, idx) => (
                                <div key={section.id || idx} className="calendar-section-item">
                                  <div className="calendar-time">
                                    {section.date && <div className="calendar-date" style={{ fontSize: '0.8em', opacity: 0.8, fontWeight: 'bold', color: 'var(--primary-blue)' }}>{formatDate(section.date)}</div>}
                                    {section.time}
                                  </div>
                                  <div className="calendar-location">
                                    {section.location}
                                    {section.meetingLink && (
                                      <div style={{ marginTop: '2px', fontSize: '0.85em', color: '#555' }}>
                                        <div><a href={section.meetingLink} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>Enlace Reunión</a></div>
                                        {section.topic && <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={section.topic}>Tema: {section.topic}</div>}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className="calendar-empty">Sin horarios</div>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              ) : (
                <div className="list-view">
                  {filteredSections.length > 0 ? (
                    filteredSections.map((section, index) => (
                      <div key={section.id || index} className="schedule-item">
                        <div className="schedule-item-content">
                          <div className="schedule-day">
                            <Clock size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                            {section.day} {section.date && <span className="section-date-badge" style={{ marginLeft: '8px', fontSize: '0.85em', background: 'var(--primary-blue)', color: 'white', padding: '2px 8px', borderRadius: '4px' }}>{formatDate(section.date)}</span>}
                          </div>
                          <div className="schedule-time">{section.time}</div>
                          <div className="schedule-location">
                            <MapPin size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                            {section.location || 'Ubicación no especificada'}
                            {section.meetingLink && (
                              <div style={{ marginTop: '8px', fontSize: '0.9em', color: '#555', paddingLeft: '22px' }}>
                                <div><strong>Enlace:</strong> <a href={section.meetingLink} target="_blank" rel="noreferrer">{section.meetingLink}</a></div>
                                {section.topic && <div><strong>Tema:</strong> {section.topic}</div>}
                              </div>
                            )}
                          </div>
                        </div>
                        {user && section.createdBy === user.uid && (
                          <button
                            className="delete-section-btn-public"
                            onClick={() => handleDeleteSection(section.id)}
                            title="Eliminar sección"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="no-sections-filtered">
                      <Filter size={32} />
                      <p>No se encontraron horarios con los filtros seleccionados</p>
                      {hasActiveFilters && (
                        <button className="clear-filters-link" onClick={clearFilters}>
                          Limpiar filtros
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Materials Modal */}
      {showMaterialsModal && (
        <div className="materials-modal-overlay" onClick={() => setShowMaterialsModal(false)}>
          <div className="materials-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <BookOpen size={28} style={{ marginRight: '12px', verticalAlign: 'middle' }} />
                Material de {currentCourse?.name}
              </h2>
              <button className="close-modal" onClick={() => setShowMaterialsModal(false)}>
                <X size={24} />
              </button>
            </div>

            <div className="modal-content">
              {/* Sticky Notes */}
              {currentCourseNotes.length > 0 && (
                <div className="sticky-notes-section">
                  <h3>
                    <StickyNoteIcon size={24} style={{ marginRight: '10px', verticalAlign: 'middle', color: '#FFD700' }} />
                    Notas Importantes
                  </h3>
                  <div className="sticky-notes-grid">
                    {currentCourseNotes.map(note => (
                      <StickyNote
                        key={note.id}
                        note={note}
                        onEdit={() => { }}
                        onDelete={() => { }}
                        onTogglePin={() => { }}
                        isOwner={false}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Materials by Category */}
              {['video', 'ejercicio', 'libro', 'simulador'].map(type => {
                const typeMaterials = materialsByType[type]
                if (typeMaterials.length === 0) return null

                const iconMap = {
                  video: <Video size={24} style={{ marginRight: '10px', verticalAlign: 'middle', color: '#667eea' }} />,
                  ejercicio: <FileText size={24} style={{ marginRight: '10px', verticalAlign: 'middle', color: '#10b981' }} />,
                  libro: <Book size={24} style={{ marginRight: '10px', verticalAlign: 'middle', color: '#f59e0b' }} />,
                  simulador: <Microscope size={24} style={{ marginRight: '10px', verticalAlign: 'middle', color: '#ec4899' }} />
                }

                const labelMap = {
                  video: 'Videos',
                  ejercicio: 'Ejercicios',
                  libro: 'Libros',
                  simulador: 'Simuladores'
                }

                return (
                  <div key={type} className="materials-category">
                    <h3>
                      {iconMap[type]}
                      {labelMap[type]}
                    </h3>
                    <div className="materials-grid">
                      {typeMaterials.map(material => (
                        <MaterialCard
                          key={material.id}
                          material={material}
                          onEdit={() => { }}
                          onDelete={() => { }}
                          isOwner={false}
                        />
                      ))}
                    </div>
                  </div>
                )
              })}

              {currentCourseMaterials.length === 0 && currentCourseNotes.length === 0 && (
                <div className="empty-materials">
                  <div style={{ fontSize: '4rem', marginBottom: '16px' }}>📚</div>
                  <p>No hay materiales disponibles aún</p>
                  <p>Los tutores agregarán recursos próximamente</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <MaterialMenu
        courseName={currentCourse?.name}
        isOpen={isMaterialMenuOpen}
        onClose={() => setIsMaterialMenuOpen(false)}
      />
    </div>
  )
}

export default Sections
